/**
 * Quick Activity Create — minimal modal for creating tasks, notes, calls.
 */

import { useState } from 'react'
import { useCRM } from '@/hooks/useCRM'
import type { ActivityType } from '@/types/crm'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  useToast,
} from '@/components/ui'

interface QuickActivityCreateProps {
  activityType: ActivityType
  contactId?: string
  dealId?: string
  onClose: () => void
  onCreated: () => void
}

const TYPE_LABELS: Record<string, string> = {
  task: 'Task',
  note: 'Note',
  call: 'Log Call',
  meeting: 'Log Meeting',
}

export function QuickActivityCreate({
  activityType, contactId, dealId, onClose, onCreated,
}: QuickActivityCreateProps) {
  const { createActivity } = useCRM()
  const { addToast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    dueDate: '',
    priority: 'normal',
  })

  const showDueDate = activityType === 'task'
  const showPriority = activityType === 'task'

  const handleSubmit = async () => {
    setIsSubmitting(true)
    const result = await createActivity({
      activityType,
      title: formData.title.trim() || undefined,
      description: formData.description.trim() || undefined,
      contactId,
      dealId,
      dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : undefined,
      priority: formData.priority,
    })
    setIsSubmitting(false)
    if (result) {
      addToast({ type: 'success', title: `${TYPE_LABELS[activityType] || activityType} created` })
      onCreated()
    } else {
      addToast({ type: 'error', title: `Failed to create ${TYPE_LABELS[activityType] || activityType}`, description: 'Check your connection and try again.' })
    }
  }

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose() }}>
      <ModalContent
        size="sm"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <ModalHeader><ModalTitle>New {TYPE_LABELS[activityType] || activityType}</ModalTitle></ModalHeader>
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit() }}>
        <ModalBody className="space-y-3">
          <Input
            label={activityType === 'note' ? 'Note' : 'Title'}
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder={activityType === 'task' ? 'e.g., Follow up on quote' : activityType === 'note' ? 'Quick note...' : `Log ${activityType}`}
            autoFocus
          />

          {activityType !== 'note' && (
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="resize-none"
                rows={3}
                placeholder="Additional details..."
              />
            </div>
          )}

          {activityType === 'note' && (
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="resize-none"
              rows={4}
              placeholder="Note details..."
            />
          )}

          {showDueDate && (
            <Input
              label="Due Date"
              type="date"
              value={formData.dueDate}
              onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
            />
          )}

          {showPriority && (
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Priority</label>
              <Select
                value={formData.priority}
                onValueChange={(v) => setFormData({ ...formData, priority: v })}
              >
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" isLoading={isSubmitting}>Create</Button>
        </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}
